import React, { useState, useEffect, memo, Fragment, useMemo, useCallback } from "react";
import { Row, Col, Form, Badge, Dropdown } from "react-bootstrap"; // Tambah Dropdown
import Card from "../../../components/Card";
import DashboardService from "../../../services/pbumku/DashboardService";
import Chart from "react-apexcharts";
import CountUp from "react-countup";

// Import jika komponen chart dipisah seperti di DashPasca
// import SKPChart from "../../components/skp-chart"; 
import { FaCertificate, FaIndustry, FaMapMarkedAlt, FaCity } from "react-icons/fa";
import StatCard from "../../dashboard/components/SKPStatCard";

const ReportHaccp = memo(() => {
  const today = new Date();
  const currentYear = today.getFullYear();
  
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  
  /* =========================
   * STATE DATA
   * ========================= */
  const [upi, setUpi] = useState(0);
  const [upn, setUpn] = useState(0);
  const [haccp, setHaccp] = useState([]);
  const [jmlHaccp, setJmlHaccp] = useState({ A: 0, B: 0, C: 0, JUMLAH: 0 });

  // SKP States (Disamakan dengan DashPasca)
  const [rekapSKP, setRekapSKP] = useState([]);
  const [limitSKP, setLimitSKP] = useState(10); // Tambahan state limit
  const [totalTerbitSKP, setTotalTerbitSKP] = useState(0);
  const [resumeSKP, setResumeSKP] = useState({ sertifikat: 0, upi: 0, provinsi: 0, kabupaten: 0 }); // Tambah state ini

  const DashService = useMemo(() => new DashboardService(), []);
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const safeParse = (val) => {
    if (val === null || val === undefined) return 0;
    const parsed = parseInt(String(val).replace(/[^0-9.-]/g, '')); 
    return isNaN(parsed) ? 0 : parsed;
  };

  const formatDate = (date, isEndOfDay = false) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    // const time = isEndOfDay ? "23:59:59" : "00:00:00";
    return `${yyyy}-${mm}-${dd} `; // ${time}
  };

  /* =========================
   * FETCH DATA (LOGIKA SKP DISAMAKAN)
   * ========================= */
  const fetchData = useCallback(async (year, limit) => {
    setLoading(true);
    const start = new Date(year, 0, 1);
    const end = year === currentYear ? today : new Date(year, 11, 31);
    const awal = formatDate(start);
    const akhir = formatDate(end, true);

    try {
      const [resUpi, resUpn, resHaccpBulan, resJmlHaccp, resRekapSKP, resTotalSKP, resResumeSKP] = await Promise.all([
        DashService.getUpi(year),
        DashService.getUpn(year),
        DashService.getHACCPBulan(awal, akhir),
        DashService.getJumlahHACCP(awal, akhir),
        DashService.getSKPPerPropinsi(awal, akhir, limit),
        DashService.getJumlahTotalSKPTerbit(awal, akhir),
        DashService.getResumeSKP(awal, akhir)
      ]);

      // HACCP Data (Tidak diubah)
      setUpi(safeParse(resUpi?.data?.nilai1));
      setUpn(safeParse(resUpn?.data?.nilai1));
      const dataHaccp = resHaccpBulan?.data?.data || [];
      setHaccp(Array.isArray(dataHaccp) ? dataHaccp : []);
      setResumeSKP(resResumeSKP?.data || {});
      const dataJml = resJmlHaccp?.data?.data || {};
      setJmlHaccp({
        A: safeParse(dataJml.A),
        B: safeParse(dataJml.B),
        C: safeParse(dataJml.C),
        JUMLAH: safeParse(dataJml.JUMLAH) || (safeParse(dataJml.A) + safeParse(dataJml.B) + safeParse(dataJml.C))
      });

          const dataSKP = resRekapSKP?.data || resRekapSKP?.data?.data || [];
      setRekapSKP(Array.isArray(dataSKP) ? dataSKP : []);
      setTotalTerbitSKP(safeParse(resTotalSKP?.data?.totalSKPTerbit));
      setResumeSKP(resResumeSKP?.data || { sertifikat: 0, upi: 0, provinsi: 0, kabupaten: 0 });

    } catch (error) {
      console.error("Fetch Error:", error);
    } finally {
      setLoading(false);
    }
  }, [DashService, currentYear]);

  useEffect(() => {
    fetchData(selectedYear, limitSKP);
  }, [selectedYear, limitSKP, fetchData]);

  const handleSelectSKP = (eventKey) => {
    setLimitSKP(eventKey === "all" ? null : Number(eventKey));
  };

  /* =========================
   * CHARTS LOGIC
   * ========================= */
  
  // 1. HACCP Trend (Original)
  const chartHaccp = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'];
    const bulanKeys = ['jan', 'feb', 'mar', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
    const getDataPerGrade = (grade) => {
      const item = haccp.find(row => row.grade === grade);
      return bulanKeys.map(key => (item ? safeParse(item[key]) : 0));
    };
    return {
      options: {
        chart: { id: `tren-haccp-${selectedYear}`, toolbar: { show: false } },
        colors: ['#008FFB', '#FF4560', '#00E396'],
        xaxis: { categories: monthNames },
        legend: { position: 'top' }
      },
      series: [
        { name: "Grade A", data: getDataPerGrade("A") },
        { name: "Grade B", data: getDataPerGrade("B") },
        { name: "Grade C", data: getDataPerGrade("C") },
      ]
    };
  }, [haccp, selectedYear]);

  // 2. HACCP Donut (Original)
  const chartDonutHaccp = useMemo(() => {
    const getSumByGrade = (grade) => {
      const item = haccp.find(row => row.grade === grade);
      if (!item) return 0;
      const bulanKeys = ['jan', 'feb', 'mar', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
      return bulanKeys.reduce((acc, key) => acc + (safeParse(item[key])), 0);
    };
    const seriesData = [getSumByGrade("A"), getSumByGrade("B"), getSumByGrade("C")];
    const grandTotal = seriesData.reduce((acc, curr) => acc + curr, 0);
    return {
      options: {
        chart: { type: "donut", id: `donut-chart-${selectedYear}` },
        labels: ["Grade A", "Grade B", "Grade C"],
        colors: ["#008FFB", "#FF4560", "#00E396"],
        plotOptions: { pie: { donut: { labels: { show: true, total: { show: true, label: "TOTAL", formatter: () => String(grandTotal) } } } } },
        legend: { position: "bottom" }
      },
      series: seriesData
    };
  }, [haccp, selectedYear]);

  // 3. SKP Chart (Updated Logic)
  const chartSKP = useMemo(() => ({
    options: {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: '80%' } },
      xaxis: { 
        categories: rekapSKP.map(item => item.uraian_propinsi || item.provinsi || "N/A"),
        labels: { style: { fontSize: '10px' } }
      },
      colors: ['#008FFB'],
      dataLabels: { enabled: true, style: { fontSize: '10px' } }
    },
    series: [{ name: 'Jumlah SKP', data: rekapSKP.map(item => safeParse(item.jumlah)) }]
  }), [rekapSKP]);

  console.log("Rekap SKP:", rekapSKP);
  console.log("Total Terbit SKP:", totalTerbitSKP);
  return (
    <Fragment>
      {/* HEADER SECTION */}
      <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-white rounded shadow-sm border-top border-primary border-4">
        <div>
          <h3 className="mb-1 fw-bold text-info">Dashboard Sertifikasi Mutu Pascapanen</h3>
          <p className="text-muted small mb-0">Statistik Laporan tahun {selectedYear}</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Form.Select 
            size="sm" 
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="border-primary shadow-sm"
            style={{ width: '120px' }} 
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </Form.Select>
          {loading && <Badge bg="warning" className="ms-2">Updating...</Badge>}
        </div>
      </div>

      <Row className="gy-4">
        {/* SKP SECTION (Updated) */}
        <Col xs="12" xl="6">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-transparent border-0 pt-3 d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-0">Jumlah SKP Per Provinsi</h5>
              <Dropdown onSelect={handleSelectSKP}>
                <Dropdown.Toggle variant="outline-primary" size="sm">
                  {limitSKP ? `Top ${limitSKP}` : "Semua"}
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Item eventKey="all">Semua</Dropdown.Item>
                  {[5, 10, 15, 20].map((v) => (
                    <Dropdown.Item key={v} eventKey={v}>{v}</Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">Memuat data...</div>
              ) : rekapSKP.length > 0 ? (
                <Fragment>
                  <Card.Body>
                                    <Row className="g-3">
                                      <Col xs={3}>
                                        <StatCard
                                          title="Sertifikat"
                                          value={resumeSKP.sertifikat || 0}
                                          icon={FaCertificate}
                                          color="#008FFB"
                                        />
                                      </Col>
                  
                                      <Col xs={3}>
                                        <StatCard
                                          title="UPI"
                                          value={resumeSKP.upi || 0}
                                          icon={FaIndustry}
                                          color="#00E396"
                                        />
                                      </Col>
                  
                                      <Col xs={3}>
                                        <StatCard
                                          title="Propinsi"
                                          value={resumeSKP.provinsi || 0}
                                          icon={FaMapMarkedAlt}
                                          color="#FEB019"
                                        />
                                      </Col>
                  
                                      <Col xs={3}>
                                        <StatCard
                                          title="Kabupaten"
                                          value={resumeSKP.kabupaten || 0}
                                          icon={FaCity}
                                          color="#775DD0"
                                        />
                                      </Col>
                                    </Row>
                                  </Card.Body>
                  <Chart options={chartSKP.options} series={chartSKP.series} type="bar" height={550} />
                  {/* <div className="text-center mt-3">
                    <div className="btn btn-primary btn-sm px-4 rounded-pill shadow-sm">
                      Total SKP: <CountUp end={totalTerbitSKP} />
                    </div>
                  </div> */}
                </Fragment>
              ) : (
                <div className="text-center py-5 text-muted">Tidak ada data SKP</div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* HACCP SECTION (Original Logic preserved) */}
        <Col xs="12" xl="6">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-transparent border-0 pt-3 text-white rounded-top" style={{backgroundColor: '#FF4560'}}>
              <h5 className="fw-bold mb-0 text-white">Statistik Sertifikat HACCP</h5>
            </Card.Header>
            <Card.Body>
              <Row className="g-2 mb-2 text-center">
                <Col md={4}>
                  <div className="p-2 rounded border border-info shadow-sm" style={{backgroundColor: '#eef2ff'}}>
                    <small className="text-info d-block text-uppercase fw-bold mb-1" style={{fontSize: '12px'}}>Tersertifikasi</small>
                    <h2 className="fw-bold text-info"><CountUp end={upi} duration={2} /></h2>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-2 rounded border border-primary shadow-sm" style={{backgroundColor: '#eef2ff'}}>
                    <small className="text-primary d-block text-uppercase fw-bold mb-1" style={{fontSize: '12px'}}>UPI Baru</small>
                    <h2 className="fw-bold text-primary"><CountUp end={upn} duration={2} /></h2>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-2 rounded border border-danger shadow-sm" style={{backgroundColor: '#fff1f2'}}>
                    <small className="text-danger d-block text-uppercase fw-bold mb-1" style={{fontSize: '12px'}}>UPI Lama</small>
                    <h2 className="fw-bold text-danger"><CountUp end={upi - upn} duration={2} /></h2>
                  </div>
                </Col>
              </Row>

              <div className="bg-light p-3 rounded mb-2 shadow-sm border">
                <h6 className="text-center fw-bold mb-2">Tren Grade Bulanan</h6>
                <Chart options={chartHaccp.options} series={chartHaccp.series} type="bar" height={200} />
              </div>
              
              <div className="bg-light p-3 rounded shadow-sm border">
                <h6 className="text-center fw-bold mb-3">Proporsi Grade HACCP</h6>
                <div className="d-flex justify-content-center">
                  <Chart key={`donut-haccp-${selectedYear}`} options={chartDonutHaccp.options} series={chartDonutHaccp.series} type="donut" width={300} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
});

export default ReportHaccp;